import React, { useState } from 'react';
import { createStyles, Grid, makeStyles, MenuItem, TextField } from '@material-ui/core';
import { Filter } from '../DataGridFilterMenu';
import DateField from '../DateField';
import { useTranslation } from '@fluentsms/i18n';

export type DataGridDateFilterPropTypes = {
  filter: Filter;
  onFilterChange: (filterValue: Filter) => void;
};

const useStyles = makeStyles(() =>
  createStyles({
    columnInputLabel: {
      textTransform: 'capitalize',
    },
  }),
);

export const DataGridDateFilter = ({ filter, onFilterChange }: DataGridDateFilterPropTypes): JSX.Element => {
  const classes = useStyles();
  // coercing to 'any' because i18n-next t-function is causing typescript
  // errors even when used correctly
  const { t }: { t: any } = useTranslation('common'); // eslint-disable-line @typescript-eslint/no-explicit-any

  const [dateError, setDateError] = useState(false);

  const handleValueChange = (date: Date | null) => {
    const validDate = date?.valueOf();
    if (validDate) {
      setDateError(false);
      onFilterChange({ ...filter, value: date || null });
    } else {
      setDateError(true);
      onFilterChange({ ...filter, value: '' || null });
    }
  };

  const handleOperatorChange = (
    e: React.ChangeEvent<{
      name?: string | undefined;
      value: unknown;
    }>,
  ) => {
    onFilterChange({ ...filter, operator: (e.target.value as string) || '' });
  };

  const isEmptyOrNotEmptyFilter = filter.operator === 'isEmpty' || filter.operator === 'isNotEmpty';

  return (
    <Grid container>
      {/* Render <select> menu of all available table columns */}
      <Grid item xs={12} sm={6}>
        <TextField
          id="operator-field"
          value={filter.operator || ''}
          label={t('operator')}
          InputLabelProps={{
            className: classes.columnInputLabel,
          }}
          onChange={handleOperatorChange}
          data-testid="data-grid-date-filter-operator-field"
          select
          fullWidth
        >
          <MenuItem key={`filter-operator-is`} value="is" data-testid="data-grid-date-filter-operator-option-is">
            is
          </MenuItem>
          <MenuItem
            key={`filter-operator-is-empty`}
            value="isEmpty"
            data-testid="data-grid-string-filter-operator-option-is-empty"
          >
            is empty
          </MenuItem>
          <MenuItem
            key={`filter-operator-is-not-empty`}
            value="isNotEmpty"
            data-testid="data-grid-string-filter-operator-option-is-not-empty"
          >
            is not empty
          </MenuItem>
          <MenuItem
            key={`filter-operator-is-before`}
            value="isBefore"
            data-testid="data-grid-date-filter-operator-option-isBefore"
          >
            is before
          </MenuItem>
          <MenuItem
            key={`filter-operator-is-after`}
            value="isAfter"
            data-testid="data-grid-date-filter-operator-option-isAfter"
          >
            is after
          </MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6}>
        <DateField
          data-testid="date-field-filter"
          disabled={isEmptyOrNotEmptyFilter}
          label={t('value')}
          fullWidth
          error={dateError}
          InputLabelProps={{
            className: classes.columnInputLabel,
          }}
          DatePickerProps={{
            disabled: isEmptyOrNotEmptyFilter,
            maxDate: new Date('2100-1-1'),
          }}
          KeyboardButtonProps={{
            disabled: isEmptyOrNotEmptyFilter,
          }}
          value={
            isEmptyOrNotEmptyFilter
              ? null
              : filter.value && !Array.isArray(filter.value)
              ? new Date(filter.value)
              : null
          }
          onChange={handleValueChange}
        />
      </Grid>
    </Grid>
  );
};
